// /index.js

// --- 各機能のインポート ---
import { toggleElementsByPosition, adjustByHeaderHeight, observeHeaderHeight, initAnchorLinkInterceptor, scrollToId, handleHashOnLoad } from "./common/scrollUtils.js";

import { initCareerAccordion } from "./common/accordion.js";
import { initModal } from "./common/modal.js";
import { observeFade } from "./effects/fadeObserver.js";

// --- ユーティリティ：トランジション終了待ち ---
function waitForTransitionEnd(el, { timeout = 600 } = {}) {
  return new Promise((resolve) => {
    if (!el) return resolve();
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    const timer = setTimeout(finish, timeout);
    el.addEventListener(
      "transitionend",
      () => {
        clearTimeout(timer);
        finish();
      },
      { once: true }
    );
  });
}

// --- 共通の初期化処理 ---
document.addEventListener("DOMContentLoaded", () => {
  // ヘッダーやメニュー関連の要素取得
  const header = document.querySelector(".l-header");
  const mvSection = document.querySelector(".js-mv");
  const hamburger = document.querySelector(".l-header__hamburger");
  const menuBox = document.querySelector(".l-header__box");
  const menuBg = document.querySelector(".l-header__bg");
  const navLinks = document.querySelectorAll(".l-header__nav a");

  // ヘッダー高さ追従（ResizeObserver対応）
  observeHeaderHeight(header, mvSection, menuBg);

  // 表示制御対象の要素（スクロールで出現）
  const elementsToToggle = [
    { element: document.querySelector(".js-sns"), initialClass: "hidden" },
    { element: document.querySelector(".js-page-top"), initialClass: "hidden" },
  ];
  elementsToToggle.forEach(({ element, initialClass }) => {
    if (element) element.classList.add(initialClass);
  });

  // --- スクロールで表示切替 ---
  window.addEventListener(
    "scroll",
    () => {
      toggleElementsByPosition(header, mvSection, elementsToToggle);
    },
    { passive: true }
  );
  toggleElementsByPosition(header, mvSection, elementsToToggle);

  // --- アンカーリンク（本文内：URLに # を残さない） ---
  initAnchorLinkInterceptor(document);

  // --- 別ページからの # 着地対応（安定スクロール & # 除去） ---
  handleHashOnLoad();

  // --- ハンバーガーメニューの開閉処理 ---
  hamburger?.addEventListener("click", () => {
    const isActive = hamburger.classList.toggle("active");
    menuBox.classList.toggle("active");
    menuBg.classList.toggle("active");
    document.body.style.overflow = isActive ? "hidden" : "";
  });

  // --- ヘッダー内ナビ：閉じて→待って→スクロール（本文横取りは使わない）
  navLinks.forEach((link) => {
    link.addEventListener("click", async (e) => {
      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#")) {
        // 外部/別ページ遷移はメニューだけ閉じて通常遷移
        hamburger?.classList.remove("active");
        menuBox?.classList.remove("active");
        menuBg?.classList.remove("active");
        document.body.style.overflow = "";
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const id = href.slice(1);

      // 1) 閉じる
      hamburger?.classList.remove("active");
      menuBox?.classList.remove("active");
      menuBg?.classList.remove("active");
      document.body.style.overflow = "";

      // 2) 背景のトランジション終了を待つ（スクロールバー復帰もこれで吸収）
      await waitForTransitionEnd(menuBg);

      // 3) フォント準備（Adobe Fonts）を待ち、次フレームで同期
      try {
        await (document.fonts?.ready ?? Promise.resolve());
      } catch {}
      await new Promise((r) => requestAnimationFrame(r));

      // 4) 最終スクロール（#は残さない）
      await scrollToId(id, { keepHash: false });
    });
  });

  // 背景クリックで閉じる
  menuBg?.addEventListener("click", (e) => {
    if (e.target === menuBg) {
      hamburger?.classList.remove("active");
      menuBox?.classList.remove("active");
      menuBg?.classList.remove("active");
      document.body.style.overflow = "";
    }
  });

  // --- FAQの開閉 ---
  initCareerAccordion();

  // --- 画像モーダル ---
  initModal();

  // --- フェードアニメーション ---(fade-area)
  observeFade(".b-animation--fade-up");
  observeFade(".b-animation--fade-left");
  observeFade(".b-animation--fade-right");
  observeFade(".b-animation--fade-mv", {}, true);
  observeFade(".b-animation--file-in", {}, true);
  observeFade(".b-animation--slide-in-left");
  observeFade(".b-animation--slide-in-right");
  observeFade(".b-animation--slide-in-up");
  observeFade(".b-animation--slide-in-down");
  observeFade(".b-animation--zoom-in");

  // ▼ 地図のズーム演出（別処理）
  const mapZoomTarget = document.querySelector(".js-mapZoom");
  if (mapZoomTarget) {
    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (entry.isIntersecting) {
          mapZoomTarget.classList.add("is-visible");
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(mapZoomTarget);
  }

  // --- Luxy.js（必要に応じて） ---
  // initLuxy();
});

// リサイズでヘッダー変化に追従
window.addEventListener("resize", () => {
  const header = document.querySelector(".l-header");
  const mvSection = document.querySelector(".js-mv");
  const menuBg = document.querySelector(".l-header__bg");
  adjustByHeaderHeight(header, mvSection, menuBg);
});
